import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel,
  MenuItem, Pagination, Select, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { episodesAPI } from '../../services/api';
import EpisodeRotation from './EpisodeRotation';

/**
 * Published-episodes index. One row per WP post that has
 * `_autopod_published_at` set, with click-through links to every
 * platform we publish to: WP page, WP admin, Megaphone CMS, RSS feed,
 * Apple Podcasts, Spotify, YouTube (watch + Studio), raw audio URL.
 *
 * Backend: GET /api/v1/episodes/published-episodes — joins WP postmeta
 * with megaphone.episodes + megaphone.shows for the platform IDs.
 */
interface MumMetrics {
  today_downloads:   number | null;
  today_streams:     number | null;
  today_delivery:    number | null;
  last_7d_downloads: number | null;
  last_7d_streams:   number | null;
  last_7d_delivery:  number | null;
  total_downloads:   number | null;
  total_streams:     number | null;
  total_delivery:    number | null;
}

interface PublishedEpisode {
  site:             'spoutlore' | 'mallbrats';
  post_id:          number;
  post_title:       string;
  post_status:      string;
  post_date:        string | null;
  season:           number | null;
  episode:          number | null;
  thumbnail_url:    string | null;
  description:      string;
  published_at:     string | null;

  wp_url:           string;
  wp_edit_url:      string;

  megaphone_id:               string | null;
  megaphone_cms_edit_url:     string | null;
  megaphone_feed_url:         string | null;
  megaphone_duration_seconds: number | null;
  megaphone_status:           string | null;
  megaphone_is_published:     boolean | null;
  audio_url:                  string | null;

  apple_url:                  string | null;
  spotify_url:                string | null;

  youtube_id:                 string | null;
  youtube_watch_url:          string | null;
  youtube_studio_url:         string | null;

  mum:                        MumMetrics | null;
}

const fmtCount = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
  return String(n);
};

const PAGE_SIZE = 20;

const formatDuration = (sec: number | null): string => {
  if (!sec || sec <= 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0
    ? `${h}h${m.toString().padStart(2, '0')}m`
    : `${m}m${s.toString().padStart(2, '0')}s`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const SiteChip: React.FC<{ site: string }> = ({ site }) => {
  const color = site === 'spoutlore' ? '#00ff41' : '#00ffff';
  const label = site === 'spoutlore' ? 'SL' : 'MB';
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: 'transparent', color, border: `1px solid ${color}`,
        fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75em',
      }}
    />
  );
};

/**
 * Platform pill. Color-coded by brand so the user can scan for the
 * platform they want without reading labels. Renders as a real link
 * (target=_blank) when href is provided, greyed out otherwise.
 */
const PlatformPill: React.FC<{
  href:  string | null;
  label: string;
  color: string;        // brand color (hex)
  bg?:   string;        // optional bg tint
}> = ({ href, label, color, bg }) => {
  const enabled = !!href;
  return (
    <Button
      component={enabled ? 'a' : 'span'}
      {...(enabled ? { href, target: '_blank', rel: 'noreferrer' } : {})}
      size="small"
      disabled={!enabled}
      title={enabled ? `Open ${label}` : `${label} — not available yet`}
      sx={{
        minWidth: 0,
        px: 1.1, py: 0.25,
        fontSize: '0.7em',
        fontFamily: 'monospace',
        fontWeight: 700,
        letterSpacing: '0.05em',
        color: enabled ? color : 'text.disabled',
        borderColor: enabled ? color : 'rgba(255,255,255,0.12)',
        border: '1px solid',
        bgcolor: enabled ? (bg || 'transparent') : 'transparent',
        '&:hover': enabled
          ? { bgcolor: color + '20', borderColor: color }
          : undefined,
        textTransform: 'none',
        lineHeight: 1.2,
        '&.Mui-disabled': {
          color: 'text.disabled',
          borderColor: 'rgba(255,255,255,0.08)',
        },
      }}
    >
      {label}
    </Button>
  );
};

const PublishedEpisodesTab: React.FC = () => {
  const [site, setSite] = useState<'all' | 'spoutlore' | 'mallbrats'>('all');
  const [page, setPage] = useState(1);
  const [thumbsFor, setThumbsFor] = useState<string | null>(null);
  const offset = (page - 1) * PAGE_SIZE;

  const { data, isLoading, error } = useQuery({
    queryKey: ['published-episodes', site, offset],
    queryFn: () => episodesAPI.publishedEpisodes({
      site: site === 'all' ? undefined : site,
      limit: PAGE_SIZE,
      offset,
    }),
  });

  const episodes: PublishedEpisode[] = data?.episodes ?? [];
  const total: number = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
          // PUBLISHED EPISODES
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Show</InputLabel>
          <Select
            label="Show"
            value={site}
            onChange={(e) => { setSite(e.target.value as any); setPage(1); }}
          >
            <MenuItem value="all">All shows</MenuItem>
            <MenuItem value="spoutlore">Spout Lore</MenuItem>
            <MenuItem value="mallbrats">Mall Brats</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        {!isLoading && (
          <Typography variant="caption" color="text.secondary">
            {total} published episode{total === 1 ? '' : 's'}
          </Typography>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load: {(error as any)?.message || 'unknown error'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer sx={{ borderColor: 'divider' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}>Show / SxxEyy</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell sx={{ width: 100 }}>Published</TableCell>
                  <TableCell sx={{ width: 80 }}>Duration</TableCell>
                  <TableCell sx={{ width: 175 }} align="right">
                    Downloads
                    <Box sx={{ fontSize: '0.65em', opacity: 0.6, fontWeight: 400 }}>
                      today · 7d · all-time
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 420 }}>Platforms</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {episodes.map((ep) => {
                  const epKey = ep.season != null && ep.episode != null
                    ? `S${String(ep.season).padStart(2, '0')}E${String(ep.episode).padStart(2, '0')}`
                    : null;
                  const rowId = `${ep.site}-${ep.post_id}`;
                  return (
                  <React.Fragment key={rowId}>
                  <TableRow hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <SiteChip site={ep.site} />
                        {epKey && (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {epKey}
                          </Typography>
                        )}
                      </Stack>
                      {epKey && (
                        <Button size="small" sx={{ fontSize: 10, p: 0, minWidth: 0, mt: 0.5 }}
                                onClick={() => setThumbsFor(thumbsFor === rowId ? null : rowId)}>
                          {thumbsFor === rowId ? 'hide thumbnails' : 'thumbnails'}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        {ep.thumbnail_url && (
                          <Box
                            component="a"
                            href={ep.wp_url}
                            target="_blank"
                            rel="noreferrer"
                            title="Open live WP page"
                            sx={{ flexShrink: 0, lineHeight: 0 }}
                          >
                            <Box
                              component="img"
                              src={ep.thumbnail_url}
                              alt=""
                              sx={{
                                width: 48, height: 48, objectFit: 'cover',
                                border: '1px solid', borderColor: 'divider',
                                borderRadius: 0.5,
                                '&:hover': { borderColor: 'primary.main' },
                              }}
                            />
                          </Box>
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <Box
                              component="a"
                              href={ep.wp_url}
                              target="_blank"
                              rel="noreferrer"
                              title="Open live WP page"
                              sx={{
                                color: 'inherit', textDecoration: 'none',
                                '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                              }}
                            >
                              {ep.post_title}
                            </Box>
                          </Typography>
                          {ep.description && (
                            <Typography variant="caption" color="text.secondary"
                                        sx={{ display: '-webkit-box', WebkitLineClamp: 2,
                                              WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {ep.description}
                            </Typography>
                          )}
                          {ep.megaphone_status && ep.megaphone_status !== 'complete' && (
                            <Chip label={`MP: ${ep.megaphone_status}`} size="small"
                                  sx={{ mt: 0.5, fontSize: '0.65em', height: 18 }}
                                  color="warning" variant="outlined" />
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {formatDate(ep.published_at || ep.post_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {formatDuration(ep.megaphone_duration_seconds)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {ep.mum ? (
                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.8em', lineHeight: 1.4 }}>
                          <Box>
                            <strong style={{
                              color: (ep.mum.today_delivery ?? 0) > 0 ? '#00ff41' : 'inherit',
                            }}>
                              {fmtCount(ep.mum.today_delivery)}
                            </strong>
                            {' · '}
                            {fmtCount(ep.mum.last_7d_delivery)}
                            {' · '}
                            {fmtCount(ep.mum.total_delivery)}
                          </Box>
                          {(ep.mum.total_streams ?? 0) > 0 && (
                            <Box sx={{ fontSize: '0.85em', opacity: 0.6 }}>
                              {fmtCount(ep.mum.total_streams)} Spotify
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{
                          fontFamily: 'monospace', opacity: 0.4,
                        }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.75}>
                        {/* VIEW links — where listeners go */}
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="caption" sx={{
                            fontFamily: 'monospace', opacity: 0.55, mr: 0.5, minWidth: 36,
                          }}>
                            VIEW
                          </Typography>
                          <PlatformPill href={ep.wp_url}            label="WP page"  color="#00ff41" />
                          <PlatformPill href={ep.apple_url}         label="Apple"    color="#fc3c44" />
                          <PlatformPill href={ep.spotify_url}       label="Spotify"  color="#1DB954" />
                          <PlatformPill href={ep.youtube_watch_url} label="YouTube"  color="#ff0000" />
                          <PlatformPill href={ep.megaphone_feed_url} label="RSS"     color="#ff9000" />
                          <PlatformPill href={ep.audio_url}         label=".mp3"     color="#a0a0a0" />
                        </Stack>
                        {/* EDIT links — where I go to fix something */}
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="caption" sx={{
                            fontFamily: 'monospace', opacity: 0.55, mr: 0.5, minWidth: 36,
                          }}>
                            EDIT
                          </Typography>
                          <PlatformPill href={ep.wp_edit_url}            label="WP admin"     color="#21759b" />
                          <PlatformPill href={ep.megaphone_cms_edit_url} label="Megaphone CMS" color="#8b5cf6" />
                          <PlatformPill href={ep.youtube_studio_url}     label="YT Studio"     color="#ff0000" />
                        </Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {thumbsFor === rowId && epKey && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ bgcolor: 'action.hover' }}>
                        <EpisodeRotation episodeKey={epKey} site={ep.site} />
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                  );
                })}
                {episodes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        No published episodes
                        {site !== 'all' ? ` for ${site}` : ''}
                        {' '}yet. Use the Publish tab to ship your first.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pageCount > 1 && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                size="small"
              />
            </Stack>
          )}

          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', mt: 2 }}>
            <strong>VIEW</strong> row = where listeners consume the episode.{' '}
            <strong>EDIT</strong> row = where you go to fix something.{' '}
            Greyed-out pills = link not available yet (e.g. Apple/Spotify
            URLs only appear after Megaphone has synced its catalog entry,
            which can take ~1 day after first publish).
          </Typography>
        </>
      )}
    </Box>
  );
};

export default PublishedEpisodesTab;
